/**
 * states.module.css — the app frame and the four state patterns.
 *
 * T37 owns exactly one stylesheet, so this file carries both halves of the shell:
 * `Shell.tsx`'s layout frame (the `--app-h` shell, the capped column, the sticky
 * header, the scroller and its 100px dock padding) and `states/*`'s empty, loading,
 * error and offline patterns. They belong together anyway — the reason every state
 * occupies the same grid as the content it replaces is that both are laid out by the
 * same `.frame` rule below, and splitting them across two files is how that stops
 * being true.
 *
 * The load-bearing ideas, from `docs/design-direction.md`:
 *
 *  · **One frame, five states.** `.frame` + one grid class is the whole layout of a
 *    screen region. `data-state` says which of loading / empty / error / offline /
 *    content is showing, and **no rule in this file changes a layout property based on
 *    `data-state`** — that is what makes "nothing jumps when the data arrives" a
 *    property of the CSS rather than a hope (§8.7–§8.10, §13's checklist).
 *  · **No spinner, no shimmer** (§8.8, §12.7). Loading is the one-drop ruler
 *    advancing, 1px rules drawing in from the left over `--t16` staggered 24ms, and a
 *    halftone field drifting behind them. There is no gradient sweep anywhere in this
 *    file, and `bl-state-drift` moves a `transform`, never a `background-position`,
 *    because §10.2.3 licenses only `transform`, `opacity` and `clip-path` to animate.
 *  · **No illustration** (§8.7, §12.8). Every object here is drawn from the system's
 *    own vocabulary — a record, a sleeve card, a sheet of label stock, a rubber stamp
 *    — with CSS only. There is not one `<img>`, one `<svg>` illustration or one emoji
 *    in the whole state layer.
 *  · **Two radii, one spacing scale, no ambient shadow, no green** (§5.1, §5.3, §5.5,
 *    §12.1). The mini-player's licensed shadow is `.miniplayer` in `tokens.css` and is
 *    the only one in the app; nothing here casts a shadow.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   THE SHELL — §5.2, §9.2, §10.1.
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * Height comes from Telegram, never from the viewport: `--app-h` is written from
 * `viewportStableHeight` by the façade's `syncViewport` (spec.md §13.1). `100vh`
 * inside Telegram's webview includes the strip under Telegram's own header, so a
 * `100vh` shell overflows and the bottom of the mini player is unreachable.
 *
 * `overflow: hidden` here and the scroll on `.scroll` below: exactly one scroll
 * container, so the dock can overlay it and `position: sticky` has a scrollport to
 * stick to.
 */
.shell {
  position: relative;
  display: flex;
  flex-direction: column;
  block-size: var(--app-h);
  overflow: hidden;
  background: var(--bg);
  color: var(--text);
}

.scroll {
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  /* §9.2: total bottom chrome is 100px, and every scroll container pads for it. The
     dock overlays the scroller (so the mini player's licensed shadow falls across
     moving content), which is why this padding exists at all. */
  padding-block-end: calc(var(--drop) + var(--s3) + var(--drop) + env(safe-area-inset-bottom));
  overscroll-behavior: contain;
}

/* …but only when there IS a dock. `AppShell` omits it entirely on `/now` (the screen
   already shows the full transport), so on that one screen the rule above reserved
   100px of empty air below the transport and pushed it a further 100px past the fold.
   Measured at 393x852: scrollHeight 1048 -> 948. */
.shell[data-has-dock='false'] .scroll {
  padding-block-end: env(safe-area-inset-bottom);
}

/*
 * §5.2: single column, capped at 480px, pinned **left of centre by −8px** on a wide
 * viewport. That −8px is what kills the centred-card default on desktop Telegram.
 * `position: relative` + `inset-inline-start` rather than a `transform`, because a
 * transformed ancestor becomes the containing block for `position: fixed` children
 * and would trap anything the screens fix to the viewport.
 *
 * No horizontal padding: the gutter belongs to the header and to `.frame`, so a
 * full-bleed object — a flyer divider, an edge-cropped stamp — can still reach the
 * column edge (§8.2, §6.8).
 */
.column {
  inline-size: 100%;
  max-inline-size: var(--col-max);
  margin-inline: auto;
}

@media (min-width: 496px) {
  .column {
    position: relative;
    inset-inline-start: var(--col-pin);
  }
}

/*
 * The 48px sticky header of §8's screen contract: panel ground, a 1px
 * `--border-strong` bottom edge (the bar line), mono caps left and a count right,
 * never centred. The halftone underlay is applied by the caller's `Halftone`.
 */
.header {
  position: sticky;
  inset-block-start: 0;
  z-index: var(--z-sticky);
  display: flex;
  gap: var(--s3);
  align-items: center;
  justify-content: space-between;
  min-block-size: 48px;
  padding-inline: var(--gutter);
  background: var(--bg-panel);
  border-block-end: 1px solid var(--border-strong);
}

/*
 * §9.1/§9.2: the mini player (56px) sits above the nav strip (44px) and the pair
 * overlays the scroller at the bottom of the shell. `--z-mini` is the lower of the
 * two layers; the nav strip inside carries its own.
 */
.dock {
  position: absolute;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: var(--z-mini);
  padding-block-end: env(safe-area-inset-bottom);
  background: var(--bg-panel);
}

/* ────────────────────────────────────────────────────────────────────────────────
   THE FRAME — one grid, five states (§8.7–§8.10).
   Every state component and every screen region renders through `.frame` + one grid
   class. `data-state` is an attribute and not a class precisely so that no layout
   rule can key off it.
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * `minmax(0, 1fr)` and not `1fr`. Bare `1fr` is `minmax(auto, 1fr)`, whose automatic
 * minimum is MIN-CONTENT — so one child that measures wider than the viewport does not
 * overflow alone, it widens the column and every sibling module with it. That is
 * exactly how a single mis-positioned 120px plate on Now Playing pushed the title, the
 * waveform, both timecodes, the spec table and the transport out to 406.67px inside a
 * 346px box. Flooring the minimum at 0 contains the damage to the offending child.
 */
.frame {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: start;
  inline-size: 100%;
}

.body {
  display: grid;
  grid-column: 1 / -1;
  align-content: start;
}

/* The crate, history and discography ledger. No gutter and no gap: a 56px row owns
   its own 16px padding and its own hairline, and every 4th one steps up to a bar
   line (§5.2, §9.3). */
.ledger .body {
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
}

/* Now Playing and any other single stack of modules (§8.1). */
.plate,
.plain {
  padding-inline: var(--gutter);
  padding-block: var(--s4);
}

/*
 * `--s4` and not `--s6`, on this grid only.
 *
 * `design-direction.md` names `--s6` "between modules", and that is still the scale's
 * module rung — this is one screen electing a tighter one, not a redefinition. Now
 * Playing stacks EIGHT modules, so the gap is multiplied by seven: at `--s6` it spent
 * 168px, more than the transport and the spec table put together, and pushed the
 * transport strip well past the fold on a 852px viewport. At `--s4` it spends 112px.
 *
 * Whoever changes this must also change `.bandGroup` in `nowplaying.module.css`: that
 * rule lays `calc(var(--drop) - <this gap>)` on top of the gap so the air between the
 * head and the waveform band comes to exactly `--drop`, and a mismatch silently shrinks
 * or grows the one gap §8.1 pins by name.
 */
/*
 * `--plate-gap` so a screen can tune ONE number instead of fighting this selector.
 *
 * The rung is still `--s4`; Now Playing stacks eight modules, so the gap is multiplied
 * by seven and it is the cheapest px on the screen. `nowplaying.module.css` sets
 * `--plate-gap: var(--s3)` on its own `.frame` and inherits it down — a custom-property
 * override rather than a specificity duel, which is the only kind of override this file
 * can offer without `.plate .body` growing a screen-specific exception.
 */
.plate .body {
  gap: var(--plate-gap, var(--s4));
}

.plain .body {
  gap: var(--s4);
}

/* MY CRATE — 45s standing in a box, two columns (§8.5). */
.shelf {
  padding-inline: var(--gutter);
  padding-block: var(--s4);
}

.shelf .body {
  grid-template-columns: repeat(2, 1fr);
  gap: var(--s6) var(--s4);
}

/* The drawer index — dense two-column, 28px rows, hairline-ruled (§8.3). */
.index {
  padding-inline: var(--gutter);
  padding-block: var(--s4);
}

.index .body {
  grid-template-columns: repeat(2, 1fr);
  gap: 0 var(--s6);
}

/* The SET LIST sheet: the paper owns its own edges, so the frame gives it none
   (§8.4, §6.11). */
.sheet .body {
  gap: var(--s3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Shared state furniture.
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * The one mono line every state carries. Cyrillic keeps the tier and loses the
 * tracking and the forced caps: tracked uppercase Cyrillic reads as shouting and is
 * banned (§4.3, §12.16). This mirrors `.cyr` in `primitives.module.css`.
 */
:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}

.string {
  color: var(--text-2);
}

.note {
  max-inline-size: 34ch;
  color: var(--text-3);
}

/*
 * Actions are words in square outlined stamps — never an icon, never a pill, never a
 * trash can (§9.7, §12.21). 44px is the minimum target and the rhythmic unit at once
 * (§5.1, §5.6).
 */
.action {
  display: inline-flex;
  gap: var(--s2);
  align-items: center;
  justify-self: start;
  min-inline-size: var(--drop);
  min-block-size: var(--drop);
  padding-inline: var(--s4);
  color: var(--text);
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-cut);
}

.action:active {
  background: var(--bg-pressed);
}

/* ────────────────────────────────────────────────────────────────────────────────
   LOADING — §8.8. No spinner. No shimmer. Ever.
   ──────────────────────────────────────────────────────────────────────────────── */

.status {
  display: flex;
  gap: var(--s2);
  align-items: baseline;
  padding-inline: var(--gutter);
  padding-block: var(--s3);
  color: var(--text-3);
}

/* The blinking 1ch block caret, `steps(1)` at `--t4` — the only thing `--t4` is
   for (§7.1, §8.8). Opacity only. */
@keyframes bl-state-caret {
  0%,
  49% {
    opacity: 1;
  }

  50%,
  100% {
    opacity: 0;
  }
}

.caret {
  display: inline-block;
  inline-size: 1ch;
  block-size: 0.9em;
  background: var(--accent-quiet);
  animation: bl-state-caret var(--t4) steps(1) infinite;
}

/* Real progress only. If the API does not return it, nothing numeric is printed —
   no fabricated counters, not once (§8.8, §12.6). */
.progress {
  margin-inline-start: auto;
  color: var(--text-2);
}

/*
 * The skeleton block. The halftone dot field behind it drifts, which is the
 * designed alternative to a shimmer sweep: printing, not shimmering (§8.8). The
 * drift is a `transform` on the halftone pseudo-element rather than the
 * `background-position` §8.8 sketches, because only `transform`, `opacity` and
 * `clip-path` may animate in Telegram's Android webview (§10.2.3). `.halftone`
 * itself sets `transform: rotate(15deg)`, so the keyframes carry it through.
 */
@keyframes bl-state-drift {
  from {
    transform: rotate(15deg) translate3d(0, 0, 0);
  }

  to {
    transform: rotate(15deg) translate3d(4px, 4px, 0);
  }
}

.drift::before {
  animation: bl-state-drift 900ms linear infinite alternate;
}

/*
 * The real 56px row, with everything replaced by 1px rules (§8.8). The geometry is
 * §9.3's track row verbatim — `min-height: 56px`, `padding: 6px 16px`, a 1px divider
 * stepping to a bar line every 4th — because a skeleton that is not dimensionally
 * identical to the row it stands in for is exactly the jump this whole pattern exists
 * to prevent. `6px` is the one raw value in this file and it is quoted, not chosen.
 */
.skeleton {
  display: grid;
  grid-template-columns: var(--drop) 1fr;
  gap: var(--s3);
  align-items: center;
  min-block-size: calc(var(--drop) + var(--s3));
  padding-inline: var(--gutter);
  border-block-end: 1px solid var(--border);
}

.skeleton:nth-child(4n) {
  border-block-end-color: var(--border-strong);
}

.chip {
  inline-size: var(--drop);
  block-size: var(--drop);
  border: 1px solid var(--border);
  border-radius: var(--r-round);
}

.lines {
  display: grid;
  gap: var(--s2);
}

/* Drawing in from the left over `--t16`, staggered 24ms — a rule being set, not a
   block being faked (§8.8, §7.2 TYPESETTING CUT). */
@keyframes bl-state-draw {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

.bar {
  block-size: 1px;
  background: var(--border);
  transform-origin: left center;
  animation: bl-state-draw var(--t16) var(--ease-in) both;
  animation-delay: calc(var(--i, 0) * 24ms);
}

.barTitle {
  inline-size: 70%;
}

.barMeta {
  inline-size: 40%;
}

/* ────────────────────────────────────────────────────────────────────────────────
   EMPTY — §8.7. One object, one mono string, at most one action.
   ──────────────────────────────────────────────────────────────────────────────── */

.object {
  display: grid;
  justify-items: start;
  padding-block-end: var(--s2);
}

/* The empty crate: a 200px dubplate whose label is completely blank cream — no
   print, no rim text — with the chinagraph word a plant writes on a virgin acetate
   (§8.7). The groove gradient, the label and the spindle are `texture.css`'s `.disc`
   recipe; only the diameter is ours, and the two-class selector below outranks it. */
.object .blank {
  inline-size: min(56vw, 200px);
}

.object .blankLabel {
  display: grid;
  place-items: center;
}

/* One of exactly two licensed uses of Caveat: the chinagraph title on a label
   (§4.1, §12.15). It names the object, not the state — the state is the mono line
   below, in Russian. `[data-scrawl="off"]` typesets it instead of faking a script
   face (§4.4). The scrawl is the one string in the app deliberately outside the nine
   type sizes: it is handwriting, fitted 18–24px to the label, not type (§4.1). */
.scrawl {
  font-size: 1.25rem;
  color: var(--stock-2);
  transform: rotate(-3.5deg);
}

/* Empty search: one crate divider card, flipped forward. */
.dividerCard {
  inline-size: min(72%, 260px);
  block-size: 88px;
  background: var(--bg-paper-2);
  clip-path: polygon(0 4%, 100% 0, 99.4% 96%, 0.6% 100%);
  transform: rotate(-1.2deg);
}

/* Empty favorites: a 4×4 grid of empty label-stock slots, 1px dashed. Outlined
   rather than filled cream, because sixteen cream tiles would breach the inversion
   cap of §5.5.6 — an inverted block never exceeds 25% of viewport height. */
.stock {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--s2);
  inline-size: min(100%, 260px);
}

.stock span {
  aspect-ratio: 1;
  background: var(--bg-raised);
  border: 1px dashed var(--border);
  border-radius: var(--r-cut);
}

/* Empty queue: the set-list sheet with the hand numbering and no titles — quietly
   the best state in the app (§8.7). */
.sheetStub {
  inline-size: 100%;
  padding: var(--s4) var(--s4) var(--s6);
}

.numbers {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.numbers li {
  min-block-size: 28px;
  color: var(--text-on-paper);
  font-size: var(--fs-20);
  line-height: 28px;
  opacity: 0.55;
}

/* ────────────────────────────────────────────────────────────────────────────────
   ERROR — §8.9. The only place `--warn` appears.
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * `NEEDLE SKIP` torn in half by a jagged `clip-path`, the two halves 2px out of
 * register in two inks. The tear IS the misregistration, so this costs no
 * `mix-blend-mode` and does not spend the screen's single `[data-ghost]` (§6.3,
 * §6.13). It is label print in English and is `aria-hidden`: the accessible content
 * of the plate is the Russian sentence (§11).
 */
.tear {
  position: relative;
  display: block;
  margin: 0;
}

.tearTop,
.tearBottom {
  display: block;
  color: var(--danger-text);
}

.tearTop {
  clip-path: polygon(
    0 0,
    100% 0,
    100% 46%,
    88% 55%,
    76% 44%,
    61% 57%,
    47% 45%,
    33% 58%,
    19% 46%,
    8% 56%,
    0 45%
  );
}

.tearBottom {
  position: absolute;
  inset: 0;
  color: var(--accent-quiet);
  clip-path: polygon(
    0 45%,
    8% 56%,
    19% 46%,
    33% 58%,
    47% 45%,
    61% 57%,
    76% 44%,
    88% 55%,
    100% 46%,
    100% 100%,
    0 100%
  );
  transform: translate(2px, 1px);
}

.code {
  display: block;
  color: var(--warm-text);
}

.sentence {
  max-inline-size: 40ch;
  color: var(--text);
}

.plateBody {
  display: grid;
  gap: var(--s3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   OFFLINE — §8.10. The crate is still visibly there, just not reachable.
   ──────────────────────────────────────────────────────────────────────────────── */

.stale {
  opacity: 0.45;
}

/*
 * A cached row keeps full opacity and gains a 1px `--cool` left rule. `--cool` is the
 * offline / cached colour because green does not exist in this system (§12.1).
 * Attribute selectors, because the rows come from another component and this file
 * must not know its class names.
 */
.stale [data-cached='true'] {
  border-inline-start: 1px solid var(--focus);
  opacity: 1;
}

.offline {
  position: relative;
}

.stampSlot {
  position: absolute;
  inset-block-start: var(--s6);
  inset-inline-end: 0;
  z-index: var(--z-stamp);
}

/* ────────────────────────────────────────────────────────────────────────────────
   RESUME BANNER — spec.md §1.4: last track, position, one tap to continue.
   ──────────────────────────────────────────────────────────────────────────────── */

.resume {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--s1) var(--s3);
  align-items: center;
  padding: var(--s3) var(--gutter);
  background: var(--bg-panel);
  border-block: 1px solid var(--border);
}

.resumeLabel {
  grid-column: 1 / -1;
  color: var(--text-3);
}

.resumeTitle {
  min-inline-size: 0;
  color: var(--text);
}

.resumeMeta {
  grid-column: 1;
  color: var(--text-2);
}

.resumeActions {
  display: flex;
  grid-row: 2 / 4;
  grid-column: 2;
  gap: var(--s2);
  align-items: center;
}

/* The one filled control in the state layer: `--ink-000` glyphs on `--warm-fill`,
   which is the correct contrast direction (5.48:1). `--paper` on `--warm-fill` is
   2.96:1 and banned (§3.3, §12.9). */
.primary {
  color: var(--text-on-accent);
  background: var(--accent);
  border-color: var(--accent);
}

/* ────────────────────────────────────────────────────────────────────────────────
   MEMBERS ONLY — §9.8. The refusal screen's two stamps, side by side and wrapping
   on a narrow phone rather than shrinking below the 44px target.
   ──────────────────────────────────────────────────────────────────────────────── */

.gateActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s2);
  align-items: center;
}
